import { EVENT } from '@otryad/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { ErrorState, Screen, Spinner, Stepper } from '@/components/ui';
import { useTrackOnce } from '@/lib/analytics';
import { draftStore, resumePath, useOnboardingDraft, type OnboardingDraft } from '@/lib/draft';
import { texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

export const TOTAL_STEPS = 5;

/** Черновик загружен из CloudStorage/localStorage (один раз на сессию). */
export function useDraftLoaded(): boolean {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    void draftStore.load().then(() => {
      if (alive) setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);
  return loaded;
}

/** Предыдущие шаги должны быть заполнены; иначе — на первый незавершённый экран. */
export function stepReady(draft: OnboardingDraft, step: number): boolean {
  if (step >= 2 && !draft.cityId) return false;
  if (step >= 3 && draft.districtIds.length === 0) return false;
  if (step >= 4 && !draft.availability) return false;
  if (step >= 5) {
    const primaryOk = Boolean(draft.primaryActivityId && draft.squadSize);
    if (!primaryOk && !draft.waitingForProposal) return false;
  }
  return true;
}

export interface OnboardingScreenProps {
  step: number;
  title: string;
  subtitle?: string;
  children: (ctx: {
    draft: OnboardingDraft;
    update: (p: Partial<OnboardingDraft>) => void;
  }) => ReactNode;
}

/**
 * Каркас экрана онбординга: Stepper, загрузка черновика, охрана порядка шагов.
 * Справочники (`onboarding.options`) экраны запрашивают сами через `useOptions`.
 */
export function OnboardingScreen({ step, title, subtitle, children }: OnboardingScreenProps) {
  const loaded = useDraftLoaded();
  const { draft, update } = useOnboardingDraft();
  const navigate = useNavigate();
  const ready = loaded && stepReady(draft, step);

  // Шаг воронки «начал онбординг» (Stage 13, задача 1): один раз за сессию
  useTrackOnce(EVENT.onboardingStarted, { step });

  useEffect(() => {
    if (loaded && !ready) void navigate(resumePath(draft.step), { replace: true });
  }, [loaded, ready, draft.step, navigate]);

  return (
    <Screen title={title} subtitle={subtitle}>
      <Stepper current={step} total={TOTAL_STEPS} labels={texts.onboarding.steps} />
      {ready ? (
        children({ draft, update })
      ) : (
        <div className="flex flex-1 items-center justify-center py-16">
          <Spinner />
        </div>
      )}
    </Screen>
  );
}

export function useOptions(cityId?: string) {
  return trpc.onboarding.options.useQuery(cityId ? { cityId } : {}, { staleTime: 60_000 });
}

export function OptionsState({
  query,
  children,
}: {
  query: { isPending: boolean; isError: boolean; refetch: () => unknown };
  children: ReactNode;
}) {
  if (query.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (query.isError) return <ErrorState onRetry={() => void query.refetch()} />;
  return <>{children}</>;
}
